import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Badge, PageLoading, Select } from '../../components/ui';
import type {
  CompanySettings, Lead, LeadStage, LeadStatus, TeamMember, CustomerFormRoute,
} from '../../types';

const CAT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  open: 'warning', won: 'success', lost: 'danger',
};

export default function LeadDetail({
  leadId, onClose, onChanged,
}: { leadId: string; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const { can } = useAuth();
  const canManage = can('manager', 'company_admin', 'platform_admin');

  const [lead, setLead] = useState<Lead | null>(null);
  const [stages, setStages] = useState<LeadStage[]>([]);
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [routes, setRoutes] = useState<CustomerFormRoute[]>([]);
  const [customersOn, setCustomersOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertRoute, setConvertRoute] = useState('');

  const load = useCallback(async () => {
    try {
      setLead(await api.get<Lead>(`/leads/${leadId}`));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not open the lead.');
    }
  }, [leadId, toast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [st, ss] = await Promise.all([
          api.get<LeadStage[]>('/leads/stages'),
          api.get<LeadStatus[]>('/leads/statuses'),
        ]);
        setStages(st); setStatuses(ss);
      } catch { /* dropdowns are a convenience */ }
      if (canManage) {
        try { setTeam(await api.get<TeamMember[]>('/users')); } catch { /* ignore */ }
      }
      try {
        const s = await api.get<CompanySettings>('/settings');
        setCustomersOn(s.customersEnabled);
      } catch { /* ignore */ }
    })();
  }, [canManage]);

  async function patch(body: Record<string, unknown>, msg: string) {
    setBusy(true);
    try {
      setLead(await api.patch<Lead>(`/leads/${leadId}`, body));
      toast.success(msg);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally { setBusy(false); }
  }

  async function reassign(ownerId: string) {
    if (!ownerId) return;
    setBusy(true);
    try {
      setLead(await api.post<Lead>(`/leads/${leadId}/reassign`, { ownerId }));
      toast.success('Lead reassigned.');
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not reassign.');
    } finally { setBusy(false); }
  }

  async function convert() {
    if (!convertRoute) return;
    setBusy(true);
    try {
      const r = await api.post<{ customer: { name: string } }>(`/leads/${leadId}/convert`, { routeId: convertRoute });
      toast.success(`Converted to customer: ${r.customer.name}.`);
      setConverting(false);
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not convert.');
    } finally { setBusy(false); }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.del(`/leads/${leadId}`);
      toast.success('Lead removed.');
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove.');
      setBusy(false);
    }
  }

  const converted = !!lead?.converted;

  return (
    <Modal
      title={lead?.name ?? 'Lead'}
      subtitle={lead ? `Added ${new Date(lead.createdAt).toLocaleDateString()}${lead.createdBy ? ` by ${lead.createdBy}` : ''}` : undefined}
      wide
      onClose={onClose}
      footer={(
        <>
          {canManage && !converted ? (
            <button type="button" className="btn btn-danger-ghost" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </>
      )}
    >
      {lead === null ? <PageLoading /> : (
        <div className="col gap-16">
          {converted ? (
            <div className="card card-pad" style={{ borderColor: 'var(--success)', background: 'var(--success-soft)' }}>
              <span className="row gap-8" style={{ alignItems: 'center' }}>
                <Icon name="check" size={16} />
                <span className="small">This lead has been converted to a customer.</span>
              </span>
            </div>
          ) : null}

          <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 170 }}>
              <Select label="Stage" value={lead.stage?.id ?? ''} disabled={busy || converted}
                onChange={(e) => void patch({ stageId: e.target.value }, 'Stage updated.')}>
                {stages.filter((s) => s.isActive || s.id === lead.stage?.id)
                  .map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
            </div>
            <div style={{ flex: 1, minWidth: 170 }}>
              <Select label="Status" value={lead.status?.id ?? ''} disabled={busy || converted}
                onChange={(e) => void patch({ statusId: e.target.value }, 'Status updated.')}>
                {statuses.filter((s) => s.isActive || s.id === lead.status?.id)
                  .map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
            </div>
          </div>

          <div className="row gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {lead.status?.category ? (
              <Badge tone={CAT_TONE[lead.status.category] ?? 'neutral'}>{lead.status.category}</Badge>
            ) : null}
            <span className="small faint">Owner:</span>
            {canManage && !converted ? (
              <div style={{ minWidth: 200 }}>
                <Select value={lead.owner?.id ?? ''} disabled={busy}
                  onChange={(e) => void reassign(e.target.value)}>
                  <option value="" disabled>Reassign…</option>
                  {team.filter((u) => u.status === 'active')
                    .map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                </Select>
              </div>
            ) : <strong className="small">{lead.owner?.name ?? 'Unassigned'}</strong>}
          </div>

          {/* The captured fields. */}
          {Object.keys(lead.fields).length > 0 ? (
            <div className="card">
              <table className="data">
                <tbody>
                  {Object.entries(lead.fields).map(([key, f]) => (
                    <tr key={key}>
                      <td className="faint" style={{ width: '40%' }}>{f.label}</td>
                      <td className="primary">{f.display || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="small faint">No fields captured on this lead yet.</p>}

          {/* Convert to customer. */}
          {!converted && canManage ? (
            customersOn ? (
              converting ? (
                <div className="card card-pad col gap-8">
                  <strong className="small">Convert to a customer</strong>
                  <Select label="Area / Route *" value={convertRoute}
                    onChange={(e) => setConvertRoute(e.target.value)}>
                    <option value="">— Select an area —</option>
                    {routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </Select>
                  <div className="row gap-8">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConverting(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy || !convertRoute}
                      onClick={() => void convert()}>Convert</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }}
                  onClick={async () => {
                    setConverting(true);
                    if (!routes.length) {
                      try { setRoutes(await api.get<CustomerFormRoute[]>('/customers/routes')); } catch { /* ignore */ }
                    }
                  }}>
                  <Icon name="users" size={15} /> Convert to customer
                </button>
              )
            ) : (
              <p className="small faint">The customers module is off, so this lead cannot be converted.</p>
            )
          ) : null}
        </div>
      )}
    </Modal>
  );
}
